<template>
  <nav class="navbar" :class="{ 'navbar--expanded': localConfig.navbarExpanded }" data-test-id="navbar">
    <div class="navbar-menu-section navbar__logo">
      <NavBarItem
        label="ATLAS.ti Web"
        :tooltip-text="$t('action.toProjectOverview')"
        exact
        :to="{ name: 'projects' }"
        :highlight-enabled="false"
        font-color="black"
        shortcut="go:projects"
      >
        <template #icon>
          <AtlastiLogo />
        </template>
      </NavBarItem>
    </div>

    <transition name="fade-transition">
      <div v-if="projectId" :project-id="projectId" class="navbar-menu-section">
        <NavBarItem
          :is-fake-input="localConfig.navbarExpanded"
          :label="$t('action.search')"
          @click="openGlobalSearchModal"
          data-test-id="global-search-open"
          shortcut="global-search:open"
          :is-active-forced="isGlobalSearchActive"
        >
          <template #icon>
            <SearchIcon16 />
          </template>
        </NavBarItem>
      </div>
    </transition>

    <transition name="fade-transition">
      <div v-if="projectId" :project-id="projectId" class="navbar-menu-section">
        <NavBarItem
          :label="$t('sourceOverview.title')"
          :to="{ name: 'sources', params: { projectId } }"
          shortcut="go:sources"
          data-test-id="navbar-sources"
        >
          <template #icon>
            <DocumentIcon16 />
          </template>
        </NavBarItem>

        <NavBarItem
          :label="$t('codeOverview.title')"
          :to="{ name: 'codes', params: { projectId } }"
          shortcut="go:codes"
          data-test-id="navbar-codes"
        >
          <template #icon>
            <CodeIcon16 />
          </template>
        </NavBarItem>

        <NavBarItem
          :label="$t('quotationOverview.title')"
          :to="{ name: 'quotations', params: { projectId } }"
          shortcut="go:quotations"
          data-test-id="navbar-quotations"
        >
          <template #icon>
            <QuotationIcon16 />
          </template>
        </NavBarItem>

        <NavBarItem
          :label="$t('memoOverview.title')"
          :to="{ name: 'memos', params: { projectId } }"
          shortcut="go:memos"
          data-test-id="navbar-memos"
        >
          <template #icon>
            <MemoIcon16 />
          </template>
        </NavBarItem>
      </div>
    </transition>
    <transition name="fade-transition">
      <div v-if="projectId" :project-id="projectId" class="navbar-menu-section">
        <NavBarItem
          :label="$t('report.overviewTitle')"
          :to="{ name: 'reports', params: { projectId } }"
          shortcut="go:views"
          data-test-id="navbar-views"
        >
          <template #icon>
            <BarChartIcon16 />
          </template>
        </NavBarItem>

        <NavBarItem
          :label="$t('source.conversationalAi')"
          @click="toggleConversationalAi"
          data-test-id="navbar-conversational-ai"
          :is-active-forced="isConversationalAiOpen"
        >
          <template #icon>
            <ChatbotIcon16 />
          </template>
        </NavBarItem>

        <NavBarItem
          :label="$t('scholar.paperSearch.title')"
          :to="{ name: 'paperSearch', params: { projectId } }"
          data-test-id="navbar-paper-search"
        >
          <template #icon>
            <PaperIcon16 />
          </template>
        </NavBarItem>
      </div>
    </transition>

    <div class="navbar-menu-section navbar-menu-section--bottom">
      <transition name="fade-transition">
        <NavBarItem
          v-if="projectId"
          :label="$t('menu.projectSettings')"
          :to="{ name: 'projectsettings', params: { projectId } }"
          shortcut="go:settings"
          data-test-id="navbar-project-settings"
        >
          <template #icon>
            <SettingsIcon16 />
          </template>
        </NavBarItem>
      </transition>
      <NavBarItemTooltipAlways
        :label="$t('menu.trial', remainingTrialDays)"
        v-if="remainingTrialDays !== false"
        data-test-id="navbar-menu-trial-notice"
      >
        <template #icon>
          <AttentionOctagonIcon16 class="trial-icon" />
        </template>
        <template #popover>
          <LicenseTrialNoticeCard data-test-id="navbar-menu-trial-notice-popup" :days-left="remainingTrialDays" />
        </template>
      </NavBarItemTooltipAlways>

      <BlockLeaseButton v-if="leaseBlockable" />

      <slot name="button" />

      <SupportMenu :projectId="projectId" />

      <NavBarItem
        :label="$t(localConfig.navbarExpanded ? 'action.close' : 'action.open')"
        @click="toggleExpansion"
        data-test-id="navbar-expand-toggle"
      >
        <template #icon>
          <OpenDockIcon16 v-if="localConfig.navbarExpanded" />
          <CloseDockIcon16 v-else />
        </template>
      </NavBarItem>
    </div>

    <div class="navbar-menu-section">
      <UserMenu :user="user" />
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import { ProjectDocument, ProjectQuery } from '@/graphql/operations.gen';
import NavBarItem from './NavBarItem.vue';
import NavBarItemTooltipAlways from './NavBarItemTooltipAlways.vue';
import AtlastiLogo from './AtlastiLogo.vue';
import LicenseTrialNoticeCard from '@components/license/LicenseTrialNoticeCard.vue';
import BlockLeaseButton from '@components/license/BlockLeaseButton.vue';
import SupportMenu from '@components/NavBar/SupportMenu.vue';
import UserMenu from '@components/NavBar/UserMenu.vue';
import AttentionOctagonIcon16 from '@components/Icons/AttentionOctagonIcon16.vue';
import SettingsIcon16 from '@/components/Icons/SettingsIcon16.vue';
import DocumentIcon16 from '@/components/Icons/DocumentIcon16.vue';
import CodeIcon16 from '@/components/Icons/CodeIcon16.vue';
import QuotationIcon16 from '@/components/Icons/QuotationIcon16.vue';
import MemoIcon16 from '@/components/Icons/MemoIcon16.vue';
import BarChartIcon16 from '@/components/Icons/BarChartIcon16.vue';
import CloseDockIcon16 from '@/components/Icons/CloseDockIcon16.vue';
import OpenDockIcon16 from '@/components/Icons/OpenDockIcon16.vue';
import SearchIcon16 from '@components/Icons/SearchIcon16.vue';
import ChatbotIcon16 from '@components/Icons/ChatbotIcon16.vue';
import PaperIcon16 from '@components/Icons/PaperIcon16.vue';
import { UserState } from '@/store/types';

export default defineComponent({
  name: 'NavBar',

  components: {
    SearchIcon16,
    NavBarItem,
    NavBarItemTooltipAlways,
    AtlastiLogo,
    LicenseTrialNoticeCard,
    BlockLeaseButton,
    SupportMenu,
    UserMenu,
    AttentionOctagonIcon16,
    SettingsIcon16,
    DocumentIcon16,
    CodeIcon16,
    QuotationIcon16,
    MemoIcon16,
    BarChartIcon16,
    CloseDockIcon16,
    OpenDockIcon16,
    ChatbotIcon16,
    PaperIcon16,
  },

  props: {
    user: {
      type: Object,
      required: true,
    },

    projectId: {
      type: String,
      required: false,
      default: null,
    },
  },
  data(): {
    localConfig?: UserState['localConfig'];
  } {
    return {};
  },
  computed: {
    ...mapGetters('user', ['leaseBlockable', 'remainingTrialDays', 'localConfig']),
    isGlobalSearchActive() {
      return (
        this.$store.state.modal.modals.some((modal) => modal.type === 'globalSearch') ||
        this.$route.name === 'globalsearch'
      );
    },
    isConversationalAiOpen() {
      return this.localConfig.projects?.[this.projectId]?.isConversationalAiOpen;
    },
  },
  methods: {
    toggleExpansion() {
      this.$store.dispatch('user/setLocalConfig', {
        navbarExpanded: !this.localConfig.navbarExpanded,
      });
    },
    openGlobalSearchModal() {
      this.$store.dispatch('modal/show', { type: 'globalSearch' });
    },
    /**
     * Read the project data from the cache and open the Conversational AI.
     */
    toggleConversationalAi() {
      const projectQueryCache = this.$apollo.getClient().cache.readQuery<ProjectQuery>({
        query: ProjectDocument,
        variables: { id: this.projectId },
      });

      if (this.isConversationalAiOpen) {
        this.$store.dispatch('conversationalAi/hide', { project: projectQueryCache.project });

        return;
      }

      // get selected documents if any
      const batchSelectedSourceIds = this.localConfig.projects?.[this.projectId]?.batchSelectedEntities?.source || [];

      this.$store.dispatch('conversationalAi/show', { project: projectQueryCache.project, batchSelectedSourceIds });
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';

.navbar {
  display: flex;
  flex-direction: column;
  align-items: start;
  width: settings.$navbar-width;
  height: 100%;
  background: colors.$color-grey-100;
  border-right: 1px solid colors.$color-grey-200;
  font-size: 14px;

  &--expanded {
    width: settings.$navbar-width-expanded;
  }
}

.navbar__logo {
  .atlasti-logo {
    width: 20px;
  }
}

.trial-icon {
  color: colors.$color-red;
}

.navbar-menu-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;

  &--bottom {
    margin-top: auto;
  }

  @media (max-height: 720px) {
    padding: 4px 10px;
  }
}
</style>
